<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import Alert from "@/design/primitives/Alert.vue";
import Button from "@/design/primitives/Button.vue";
import Modal from "@/design/primitives/Modal.vue";
import type { BillableResource } from "../api/billing.types";
import { formatCount } from "../api/money";
import { useBillingMe } from "../api/use-billing";
import { useBillingI18n } from "../i18n/use-billing-i18n";
import { limitOfferKind, OFFER_ROUTE } from "../offer";

/**
 * BATEU NO TETO — e aqui está o que fazer a seguir.
 *
 * ── A REGRA, E POR QUE ELA É UM COMPONENTE ────────────────────────────────
 *
 * Um limite de plano nunca termina numa frase. O produto tem exatamente DUAS
 * ofertas — comprar um adicional (plano pago) ou escolher um plano (teste e
 * grátis) — e elas apareciam em zero das três telas onde a pessoa esbarra no
 * teto: o card de limites dizia "fale com quem administra a instalação", o canal
 * recusado mostrava um toast sem link nenhum, e o convite recusado uma linha
 * vermelha. Três becos.
 *
 * Um componente e não três blocos parecidos porque a decisão é a MESMA nos três
 * lugares, e porque a diferença entre eles ("aumentar limite" vs "escolher um
 * plano") depende do trilho — um fato da cobrança que a tela de canais não tem
 * como saber sozinha. Quem decide é `offer.ts`, que é uma função pura e tem
 * teste; isto aqui só desenha.
 *
 * ── POR QUE ELE PERGUNTA O CONTRATO SOZINHO ───────────────────────────────
 *
 * `useBillingMe()` daqui de dentro, e não uma prop `me`: as três telas que o
 * usam são de features que não podem importar a cobrança, e passar o trilho por
 * prop obrigaria cada PÁGINA a carregar o contrato inteiro só para o caso de
 * alguém tropeçar. Como ele só é montado DEPOIS da recusa (`v-if`), a consulta
 * só acontece quando há uma pergunta — e nas telas de cobrança, onde o contrato
 * já foi lido, ela cai no cache.
 *
 * ── OS NÚMEROS VÊM DA RECUSA, NÃO DO CONTRATO ─────────────────────────────
 *
 * `used`/`limit` chegam por prop porque quem os sabe com certeza é o servidor
 * que acabou de recusar: o `billing/me` em cache pode ser de trinta segundos
 * atrás, e "2 de 2 canais" a discordar da recusa que a pessoa acabou de ver é
 * pior do que não escrever número nenhum. Ausentes, cai no contrato; sem teto
 * conhecido, a linha simplesmente não é escrita.
 */
const props = withDefaults(
	defineProps<{
		resource: BillableResource;
		/** O que o servidor disse na recusa. Nulo = lê-se o contrato. */
		used?: number | null;
		limit?: number | null;
		/**
		 * COMO ELA APARECE, e os três não são gosto:
		 *
		 * `dialog` interrompe — foi um clique que acabou de ser recusado (conectar
		 * um canal). `notice` fica na tela ao lado do que falhou (o convite).
		 * `inline` é uma frase com um link, para dentro de um card que já tem
		 * título e medidor — quatro caixas de aviso empilhadas na tela de limites
		 * seriam o mesmo recado dado quatro vezes em vermelho.
		 */
		variant?: "notice" | "dialog" | "inline";
		open?: boolean;
	}>(),
	{ used: null, limit: null, variant: "notice", open: true },
);

const emit = defineEmits<{ "update:open": [boolean] }>();

const { t, resourceAmount } = useBillingI18n();
const router = useRouter();

const { data: me } = useBillingMe();

const row = computed(
	() => (me.value?.limits ?? []).find((limit) => limit.resource === props.resource) ?? null,
);

/** Preço zero é o interruptor de quem não vende este adicional — não é grátis. */
const sellable = computed(() => (row.value?.unitCents ?? 0) > 0);

const kind = computed(() =>
	limitOfferKind({
		rail: me.value?.subscription?.rail ?? null,
		hasPlan: me.value?.plan != null,
		sellable: sellable.value,
		planWaived: me.value?.subscription?.planWaived,
	}),
);

/** "2 de 2 canais do plano em uso" — a frase só existe quando os dois números existem. */
const usage = computed(() => {
	const used = props.used ?? row.value?.used ?? null;
	const total = props.limit ?? (row.value ? row.value.base + row.value.extra : null);
	if (used === null || total === null || total <= 0) return "";
	return t("limits.offer.usage", {
		used: formatCount(Math.ceil(used)),
		limit: resourceAmount(props.resource, total),
	});
});

/**
 * As chaves estão escritas POR EXTENSO em cada ramo, e não montadas a partir de
 * `kind` — `verify/keys.mjs` só reconhece as que consegue ler, e uma chave que
 * ele dá por morta é uma frase que o próximo leitor apaga debaixo de um botão
 * que funciona.
 */
const lead = computed(() => {
	if (kind.value === "extras") return t("limits.offer.extrasLead");
	if (kind.value === "plan") return t("limits.offer.planLead");
	// A frase honesta de um plano pago que não vende este adicional já existe.
	return t("limits.notSold");
});

const action = computed(() => {
	if (kind.value === "extras") return t("limits.offer.extrasAction");
	if (kind.value === "plan") return t("limits.offer.planAction");
	return t("limits.offer.noneAction");
});

const destination = computed(() => OFFER_ROUTE[kind.value]);

/**
 * `router.push` e não um `RouterLink`: no diálogo o botão tem de FECHAR o
 * diálogo além de navegar, e um link que deixa o modal montado por trás da
 * próxima tela é o bug que se descobre ao voltar.
 */
function go(): void {
	emit("update:open", false);
	void router.push(destination.value);
}
</script>

<template>
	<Modal
		v-if="variant === 'dialog'"
		:open="open"
		size="sm"
		:title="t('limits.offer.title')"
		:description="usage || lead"
		:close-label="t('action.close')"
		@update:open="emit('update:open', $event)"
	>
		<!-- Os dois números vão no `description` do diálogo, que é o nome acessível
		     que a `reka-ui` exige e a frase mais concreta que há para dar. O `lead`
		     desce para o corpo só quando não é ele que já está lá em cima. -->
		<p v-if="usage" class="text-xs text-fg-muted">{{ lead }}</p>
		<template #footer>
			<Button size="sm" data-testid="plan-limit-offer" @click="go">{{ action }}</Button>
		</template>
	</Modal>

	<Alert v-else-if="variant === 'notice'" tone="warning" :title="t('limits.offer.title')">
		<p v-if="usage">{{ usage }}</p>
		<p :class="usage ? 'mt-0.5' : ''">{{ lead }}</p>
		<Button
			class="mt-2"
			size="xs"
			variant="secondary"
			data-testid="plan-limit-offer"
			@click="go"
		>
			{{ action }}
		</Button>
	</Alert>

	<!-- Sem os números: `inline` mora debaixo de um medidor que já os desenha, na
	     unidade certa — e "1 de 1 armazenamento (GB)" repetiria em palavras
	     exatamente o arredondamento que o medidor deixou de fazer. -->
	<p v-else class="text-xs text-fg-muted">
		{{ lead }}
		<Button
			variant="link"
			size="xs"
			class="align-baseline"
			data-testid="plan-limit-offer"
			@click="go"
		>
			{{ action }}
		</Button>
	</p>
</template>
